// =============================================================================
// conversations/components/NewConversationDialog.tsx — Ponto de entrada
// "Nova conversa" do live chat (F43-S11).
//
// Duas etapas num único diálogo:
//   1. Buscar o lead pelo nome/telefone (reusa hooks/crm/useLeads.ts — mesma
//      busca da lista do CRM, nenhuma query própria recriada aqui).
//   2. Escolher a caixa — delega inteiramente a StartConversationModal
//      (MESMO componente usado na ficha do lead no CRM, DoD do slot).
//
// LGPD: telefone sempre mascarado na lista de busca (maskPhone, hooks/crm/types.ts).
// =============================================================================

import * as React from 'react';
import { createPortal } from 'react-dom';

import { maskPhone } from '../../../hooks/crm/types';
import { useLeads } from '../../../hooks/crm/useLeads';
import { cn } from '../../../lib/cn';
import { useFocusTrap } from '../../sales/components/useFocusTrap';
import type { StartConversationOnChannelResponse } from '../hooks/useStartConversationOnChannel';

import { StartConversationModal } from './StartConversationModal';

// ─── Debounce local (mesmo padrão de ChatList.tsx) ───────────────────────────

function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState<T>(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

// ─── Props ──────────────────────────────────────────────────────────────────

export interface NewConversationDialogProps {
  onClose: () => void;
  onStarted: (result: StartConversationOnChannelResponse) => void;
}

export function NewConversationDialog({
  onClose,
  onStarted,
}: NewConversationDialogProps): React.JSX.Element {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [search, setSearch] = React.useState('');
  const searchDebounced = useDebounce(search, 300);
  const [selectedLead, setSelectedLead] = React.useState<{ id: string; name: string } | null>(null);

  const { data, isLoading, isError } = useLeads({
    limit: 10,
    ...(searchDebounced ? { search: searchDebounced } : {}),
  });
  const leads = data?.data ?? [];

  useFocusTrap({ containerRef: panelRef, active: true, onEscape: onClose });

  // Etapa 2: lead escolhido — delega inteiramente ao componente compartilhado
  // com o CRM (DoD: "mesmo componente").
  if (selectedLead) {
    return (
      <StartConversationModal
        leadId={selectedLead.id}
        leadName={selectedLead.name}
        onClose={onClose}
        onStarted={onStarted}
      />
    );
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center p-4"
      style={{ background: 'color-mix(in srgb, var(--text) 60%, transparent)' }}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-conversation-title"
        className="w-full max-w-md rounded-md flex flex-col overflow-hidden max-h-[min(80vh,560px)]"
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          border: '1px solid var(--border)',
          animation: 'fade-up 200ms var(--ease-out) both',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-start justify-between px-6 py-5 shrink-0"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <h2
            id="new-conversation-title"
            className="font-display font-bold text-ink"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
          >
            Nova conversa
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className={cn(
              'p-2 rounded-sm text-ink-3 hover:text-ink hover:bg-surface-hover',
              'transition-colors duration-fast',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40',
            )}
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path d="M3 3l10 10M13 3L3 13" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col gap-3 px-6 py-5 overflow-y-auto">
          <input
            type="text"
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou telefone…"
            aria-label="Buscar lead"
            className={cn(
              'w-full font-sans text-sm text-ink',
              'bg-surface-1 rounded-sm px-[14px] py-[10px]',
              'border border-border-strong',
              'shadow-[inset_0_1px_2px_var(--border-inner-dark)]',
              'placeholder:text-ink-4',
              'focus:outline-none focus:border-azul',
            )}
          />

          {isLoading && (
            <div className="flex flex-col gap-2" aria-hidden="true">
              {[1, 2, 3].map((n) => (
                <div
                  key={n}
                  className="h-12 rounded-sm animate-pulse"
                  style={{ background: 'var(--surface-muted)' }}
                />
              ))}
            </div>
          )}

          {isError && !isLoading && (
            <p className="font-sans text-sm text-danger">Não foi possível buscar contatos.</p>
          )}

          {!isLoading && !isError && searchDebounced.trim().length === 0 && (
            <p className="font-sans text-xs text-ink-4">
              Digite o nome ou telefone do contato que você quer falar.
            </p>
          )}

          {!isLoading && !isError && searchDebounced.trim().length > 0 && leads.length === 0 && (
            <p className="font-sans text-sm text-ink-3">Nenhum contato encontrado.</p>
          )}

          {!isLoading && leads.length > 0 && (
            <div className="flex flex-col gap-1">
              {leads.map((lead) => (
                <button
                  key={lead.id}
                  type="button"
                  onClick={() => setSelectedLead({ id: lead.id, name: lead.name })}
                  className={cn(
                    'w-full text-left rounded-sm px-3 py-2.5',
                    'border border-transparent',
                    'transition-colors duration-fast',
                    'hover:bg-surface-hover hover:border-border-subtle',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/30',
                  )}
                >
                  <p className="font-sans text-sm font-medium text-ink truncate">{lead.name}</p>
                  <p className="font-mono text-xs text-ink-3">{maskPhone(lead.phone_e164)}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
